/**
 * HITL-1747. Reuse of the modifier tree `buildFullMenuUnifiedItem` builds.
 *
 * A DQ burger expands to ~154,500 options (combo sizes × drinks × Blizzard
 * upgrades × mix-ins, ~98 MB of objects), and the AI's hypothesis replay
 * re-adds every cart item on each update, so the same tree was built 5–7 times
 * per burger order on the main thread — the console froze and guest audio
 * dropped each time.
 *
 * Only the `modifierGroups` map is stored; callers still get a fresh top-level
 * object per call. Sharing the map is safe because it is read-only: selections
 * are written to the cart line's own `childModifierGroups` copies.
 *
 * An entry is reused only when the same brand menu, 86 list and meal-period
 * map objects are passed again (identity), with the same scalar inputs and the
 * same set of meal periods active right now (the build's only clock read).
 *
 * The cache holds at most MAX_ENTRIES items, least recently used out first: a
 * single burger tree is ~98 MB, so it must never grow with the shift. Items in
 * the current cart share their tree with the cache, so they cost nothing extra.
 */

export const MAX_ENTRIES = 4;

export interface MenuItemBuildCacheKey {
  brandMenu: object;
  unavailableItemsByName: object;
  restaurantMealPeriodsByName?: object;
  restaurantCode: string;
  timezone?: string;
  buildViaAutoCombo: boolean;
  itemName: string;
  activeMealPeriods: string;
  // HITL-1748: a lazily built tree and an eager one never stand in for each other.
  lazy?: boolean;
}

interface Entry {
  brandMenu: object;
  unavailableItemsByName: object;
  restaurantMealPeriodsByName?: object;
  modifierGroups: unknown;
}

// Map iteration order is insertion order, so the first key is the least
// recently used one.
const entries = new Map<string, Entry>();

// The menu inputs the stored entries were built from. When a build arrives
// with different ones (a menu reload, a new 86 list, another store), every
// stored entry is dead, so they are dropped at once rather than left pinning
// ~98 MB trees and the old menu until LRU pressure happens to reach them.
let generation: {
  brandMenu: object;
  unavailableItemsByName: object;
  restaurantMealPeriodsByName?: object;
} | null = null;

const isCurrentGeneration = (key: MenuItemBuildCacheKey) =>
  !!generation &&
  generation.brandMenu === key.brandMenu &&
  generation.unavailableItemsByName === key.unavailableItemsByName &&
  generation.restaurantMealPeriodsByName === key.restaurantMealPeriodsByName;

const stringKey = (key: MenuItemBuildCacheKey) =>
  [
    key.restaurantCode,
    key.timezone || '',
    key.buildViaAutoCombo ? '1' : '0',
    key.itemName,
    key.activeMealPeriods,
    key.lazy ? 'lazy' : 'eager',
  ].join('\u0000');

export const getCachedModifierGroups = <T>(
  key: MenuItemBuildCacheKey
): T | undefined => {
  const id = stringKey(key);
  const entry = entries.get(id);
  if (
    !entry ||
    entry.brandMenu !== key.brandMenu ||
    entry.unavailableItemsByName !== key.unavailableItemsByName ||
    entry.restaurantMealPeriodsByName !== key.restaurantMealPeriodsByName
  ) {
    return undefined;
  }
  entries.delete(id);
  entries.set(id, entry);
  return entry.modifierGroups as T;
};

export const setCachedModifierGroups = <T>(
  key: MenuItemBuildCacheKey,
  modifierGroups: T
) => {
  if (!isCurrentGeneration(key)) {
    entries.clear();
    generation = {
      brandMenu: key.brandMenu,
      unavailableItemsByName: key.unavailableItemsByName,
      restaurantMealPeriodsByName: key.restaurantMealPeriodsByName,
    };
  }
  const id = stringKey(key);
  entries.delete(id);
  entries.set(id, {
    brandMenu: key.brandMenu,
    unavailableItemsByName: key.unavailableItemsByName,
    restaurantMealPeriodsByName: key.restaurantMealPeriodsByName,
    modifierGroups,
  });
  while (entries.size > MAX_ENTRIES) {
    entries.delete(entries.keys().next().value as string);
  }
};

export const clearMenuItemBuildCacheForTests = () => {
  entries.clear();
  generation = null;
};

// The most recent build, read by the main-thread stall monitor so a stall can
// be attributed to the item that was being built when it happened.
let lastItemBuild: { itemName: string; at: number; reused: boolean } | null =
  null;

export const noteItemBuild = (itemName: string, reused: boolean) => {
  lastItemBuild = { itemName, at: Date.now(), reused };
};

export const getLastItemBuild = () => lastItemBuild;
